import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormationCard, getDomainTone } from '../components/FormationCard';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { StateMessage } from '../components/StateMessage';
import { DOMAINES, NIVEAUX, VILLES } from '../data/constants';
import { useFormations } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import type { Formation } from '../types';

type FilterChipsProps<T extends string> = {
  label: string;
  options: T[];
  value: T;
  onChange: (v: T) => void;
};

function FilterChips<T extends string>({ label, options, value, onChange }: FilterChipsProps<T>) {
  return (
    <div className="cat-filter-group">
      <span className="cat-filter-label">{label}</span>
      <div className="cat-filter-chips">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            className={`cat-chip ${value === o ? 'is-on' : ''}`}
            onClick={() => onChange(o)}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<'open' | 'soldout' | 'custom' | 'none', string> = {
  open: 'mtc-pill-vert',
  soldout: 'mtc-pill-warn',
  custom: 'mtc-pill-or',
  none: 'mtc-pill-neutral',
};

function CatalogueRow({ f, onClick }: { f: Formation; onClick: () => void }) {
  const tone = getDomainTone(f.domaine);
  const moduleCount = f.programme.length;
  return (
    <button type="button" className="cat-row" onClick={onClick}>
      <div className="cat-row-img">
        <ImgPlaceholder
          label={f.image}
          ratio="1/1"
          tone={tone === 'or' ? 'creme' : tone}
          src={f.heroImage}
          alt={f.titre}
        />
      </div>
      <div className="cat-row-main">
        <div className="cat-row-meta">
          <span className={`mtc-pill mtc-pill-${tone}`}>{f.domaine}</span>
          <span className={`mtc-pill ${STATUS_PILL[f.statusTone]}`}>{f.statusLabel}</span>
          <span>{f.niveau}</span>
        </div>
        <h3>{f.titre}</h3>
        <p>{f.accroche}</p>
      </div>
      <div className="cat-row-info">
        <div className="cat-row-date">{f.moduleDatesShort}</div>
        <div className="cat-row-ville">{f.ville}</div>
        <div className="cat-row-duree">
          {moduleCount} module{moduleCount > 1 ? 's' : ''} · {f.heures}h
        </div>
      </div>
      <div className="cat-row-price">
        <div className="cat-row-num">{f.prixParModule.toLocaleString('fr-FR')}</div>
        <div className="cat-row-unit">{f.devise} HT / module</div>
      </div>
      <div className="cat-row-arrow">→</div>
    </button>
  );
}

export function Catalogue() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formationsQ = useFormations();

  useSEO({
    title: 'Toutes les formations',
    description:
      'Catalogue 2026 — implantologie, orthodontie, endodontie, parodontologie, esthétique, chirurgie orale, pédodontie. Filtres par domaine, niveau et ville.',
  });

  const [search, setSearch] = useState('');
  const [domaine, setDomaine] = useState<string>(searchParams.get('domaine') ?? 'Tous');
  const [niveau, setNiveau] = useState<string>('Tous');
  const [ville, setVille] = useState<string>('Toutes');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // Filters panel is collapsed by default — the three rows took up too much
  // vertical real estate before the first formation card was visible.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const formations = formationsQ.data ?? [];

  const filtered = useMemo(() => {
    return formations.filter((f) => {
      const q = search.toLowerCase();
      if (
        search &&
        !(f.titre.toLowerCase().includes(q) || f.domaine.toLowerCase().includes(q))
      )
        return false;
      if (domaine !== 'Tous' && f.domaine !== domaine) return false;
      if (niveau !== 'Tous' && f.niveau !== niveau) return false;
      if (ville !== 'Toutes' && f.ville !== ville) return false;
      return true;
    });
  }, [formations, search, domaine, niveau, ville]);

  const reset = () => {
    setSearch('');
    setDomaine('Tous');
    setNiveau('Tous');
    setVille('Toutes');
  };
  const activeFilterCount =
    (domaine !== 'Tous' ? 1 : 0) +
    (niveau !== 'Tous' ? 1 : 0) +
    (ville !== 'Toutes' ? 1 : 0);
  const hasFilter = activeFilterCount > 0 || search.length > 0;

  return (
    <div className="page-catalogue">
      <div className="cat-head">
        <div>
          <span className="mtc-eyebrow">Programme 2026</span>
          <h1>Toutes les formations</h1>
          <p>Filtres par domaine, niveau et ville. Sept disciplines, vingt et une sessions à venir.</p>
        </div>
      </div>

      <div className="cat-toolbar">
        <div className="cat-search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une formation, un domaine…"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="cat-search-clear">
              ×
            </button>
          )}
        </div>
        <button
          type="button"
          className={`cat-filters-toggle ${filtersOpen ? 'is-open' : ''}`}
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          aria-controls="cat-filters-panel"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M1 3h12M3 7h8M5 11h4"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <span>Filtres</span>
          {activeFilterCount > 0 && (
            <span className="cat-filters-toggle-badge">{activeFilterCount}</span>
          )}
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden="true"
            className="cat-filters-toggle-chevron"
          >
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="cat-view-toggle">
          <button
            type="button"
            className={view === 'grid' ? 'is-on' : ''}
            onClick={() => setView('grid')}
            aria-label="Grille"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect x="1" y="1" width="5" height="5" stroke="currentColor" />
              <rect x="8" y="1" width="5" height="5" stroke="currentColor" />
              <rect x="1" y="8" width="5" height="5" stroke="currentColor" />
              <rect x="8" y="8" width="5" height="5" stroke="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            className={view === 'list' ? 'is-on' : ''}
            onClick={() => setView('list')}
            aria-label="Liste"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 3h12M1 7h12M1 11h12" stroke="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      {filtersOpen && (
        <div className="cat-filters" id="cat-filters-panel">
          <FilterChips label="Domaine" options={['Tous', ...DOMAINES]} value={domaine} onChange={setDomaine} />
          <FilterChips label="Niveau" options={['Tous', ...NIVEAUX]} value={niveau} onChange={setNiveau} />
          <FilterChips label="Ville" options={['Toutes', ...VILLES]} value={ville} onChange={setVille} />
          {hasFilter && (
            <button type="button" className="cat-reset" onClick={reset}>
              Réinitialiser ×
            </button>
          )}
        </div>
      )}

      {formationsQ.isLoading ? (
        <StateMessage variant="loading" />
      ) : formationsQ.error ? (
        <StateMessage variant="error" message={String(formationsQ.error)} />
      ) : (
        <>
          <div className="cat-result-count">
            <span>
              <strong>{filtered.length}</strong> formation{filtered.length > 1 ? 's' : ''}{' '}
              {hasFilter ? 'correspondante' + (filtered.length > 1 ? 's' : '') : 'au catalogue'}
            </span>
          </div>

          {filtered.length === 0 && (
            <div className="cat-empty">
              <p>Aucune formation ne correspond à ces filtres.</p>
              <button type="button" className="mtc-btn mtc-btn-ghost" onClick={reset}>
                Réinitialiser
              </button>
            </div>
          )}

          {view === 'grid' ? (
            <div className="cat-grid">
              {filtered.map((f) => (
                <FormationCard key={f.id} f={f} onClick={() => navigate(`/formations/${f.id}`)} />
              ))}
            </div>
          ) : (
            <div className="cat-list">
              {filtered.map((f) => (
                <CatalogueRow key={f.id} f={f} onClick={() => navigate(`/formations/${f.id}`)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
